/**
 * MOTOR DE JORNADA - lògica compartida per ClockPanel, Kiosk i Supervisor
 * Determina l'estat d'un treballador i l'acció suggerida
 */

export const JOURNEY_STATES = {
  NOT_STARTED: "not_started",
  WORKING: "working",
  ON_BREAK: "on_break",
  FINISHED: "finished",
  INCONSISTENT: "inconsistent",
};

export const STATE_CONFIG = {
  not_started: {
    label: "No ha fitxat",
    color: "bg-gray-100 text-gray-600 border-gray-200",
    dot: "bg-gray-400",
    bgCard: "bg-gray-50",
    textColor: "text-gray-600",
    icon: "circle",
  },
  working: {
    label: "Treballant",
    color: "bg-emerald-100 text-emerald-700 border-emerald-200",
    dot: "bg-emerald-500",
    bgCard: "bg-emerald-50",
    textColor: "text-emerald-700",
    icon: "check",
  },
  on_break: {
    label: "En pausa",
    color: "bg-amber-100 text-amber-700 border-amber-200",
    dot: "bg-amber-500",
    bgCard: "bg-amber-50",
    textColor: "text-amber-700",
    icon: "pause",
  },
  finished: {
    label: "Jornada tancada",
    color: "bg-blue-100 text-blue-700 border-blue-200",
    dot: "bg-blue-500",
    bgCard: "bg-blue-50",
    textColor: "text-blue-700",
    icon: "done",
  },
  inconsistent: {
    label: "Incidència",
    color: "bg-red-100 text-red-700 border-red-200",
    dot: "bg-red-500",
    bgCard: "bg-red-50",
    textColor: "text-red-700",
    icon: "alert",
  },
};

export const ACTION_CONFIG = {
  entry: {
    label: "INICIAR TORN",
    shortLabel: "Entrada",
    color: "bg-emerald-500 hover:bg-emerald-600 active:bg-emerald-700",
    confirmMsg: "Entrada registrada",
    confirmIcon: "login",
  },
  pause_start: {
    label: "INICIAR PAUSA",
    shortLabel: "Pausa",
    color: "bg-amber-400 hover:bg-amber-500 active:bg-amber-600",
    confirmMsg: "Pausa iniciada",
    confirmIcon: "pause",
  },
  pause_end: {
    label: "FINALITZAR PAUSA",
    shortLabel: "Fi Pausa",
    color: "bg-blue-500 hover:bg-blue-600 active:bg-blue-700",
    confirmMsg: "Pausa finalitzada",
    confirmIcon: "play",
  },
  exit: {
    label: "FINALITZAR TORN",
    shortLabel: "Sortida",
    color: "bg-rose-500 hover:bg-rose-600 active:bg-rose-700",
    confirmMsg: "Sortida registrada",
    confirmIcon: "logout",
  },
};

/**
 * Calcula l'estat actual d'un treballador.
 * 'records' pot contenir registres de DOS dies per suportar torns nocturns.
 * La lògica d'estat es basa en la seqüència cronològica completa.
 * Retorna { state, lastRecord, issues, entryTime, breakStartTime, workedMinutes, isNightShift }
 */
export function getJourneyState(records) {
  if (!records || records.length === 0) {
    return { state: JOURNEY_STATES.NOT_STARTED, lastRecord: null, issues: [], entryTime: null, breakStartTime: null, workedMinutes: 0, isNightShift: false };
  }

  const active = records
    .filter(r => !r.is_deleted)
    .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

  if (active.length === 0) {
    return { state: JOURNEY_STATES.NOT_STARTED, lastRecord: null, issues: [], entryTime: null, breakStartTime: null, workedMinutes: 0, isNightShift: false };
  }

  const last = active[active.length - 1];
  const issues = detectIssues(active);

  // Calculate worked minutes across full window (night shift aware)
  const workedMinutes = calcWorkedMinutes(active);

  // Find first entry (may be from yesterday in a night shift)
  const firstEntry = active.find(r => r.type === "entry");
  const entryTime = firstEntry ? firstEntry.timestamp : null;

  // Detect night shift: entry date differs from last record date
  const isNightShift = firstEntry && last &&
    firstEntry.date && last.date && firstEntry.date !== last.date;

  // Find break start time (last pause_start that has no subsequent pause_end)
  const lastPauseStart = [...active].reverse().find(r => r.type === "pause_start");
  const lastPauseEnd = [...active].reverse().find(r => r.type === "pause_end");
  const breakStartTime = (lastPauseStart &&
    (!lastPauseEnd || new Date(lastPauseStart.timestamp) > new Date(lastPauseEnd.timestamp)))
    ? lastPauseStart.timestamp : null;

  let state;
  // Only mark INCONSISTENT for real errors (not warnings)
  if (issues.some(i => i.severity === "error")) {
    state = JOURNEY_STATES.INCONSISTENT;
  } else if (last.type === "exit") {
    state = JOURNEY_STATES.FINISHED;
  } else if (last.type === "pause_start") {
    state = JOURNEY_STATES.ON_BREAK;
  } else if (last.type === "entry" || last.type === "pause_end") {
    state = JOURNEY_STATES.WORKING;
  } else {
    state = JOURNEY_STATES.NOT_STARTED;
  }

  return { state, lastRecord: last, issues, entryTime, breakStartTime, workedMinutes, isNightShift };
}

/**
 * Determina l'acció principal suggerida i l'acció secundària
 */
export function getSuggestedActions(journeyState) {
  switch (journeyState.state) {
    case JOURNEY_STATES.NOT_STARTED:
      return { primary: "entry", secondary: null };
    case JOURNEY_STATES.WORKING:
      return { primary: "exit", secondary: "pause_start" };
    case JOURNEY_STATES.ON_BREAK:
      return { primary: "pause_end", secondary: null };
    case JOURNEY_STATES.FINISHED:
      return { primary: "entry", secondary: null }; // new shift
    case JOURNEY_STATES.INCONSISTENT:
      return { primary: null, secondary: null };
    default:
      return { primary: "entry", secondary: null };
  }
}

/**
 * Detecta incidències en els registres.
 * 'records' pot contenir registres de dos dies (torn nocturn):
 * els de "ahir" s'inclouen com a context per no generar falsos positius.
 * 'todayStr' és la data actual en format YYYY-MM-DD (per diferenciar torn nocturn).
 */
export function detectIssues(records, todayStr) {
  if (!records || records.length === 0) return [];
  const sorted = [...records].filter(r => !r.is_deleted).sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
  const issues = [];
  const now = new Date();

  // Check for duplicate entries (same type within 2 min)
  for (let i = 1; i < sorted.length; i++) {
    const curr = sorted[i];
    const prev = sorted[i - 1];
    const diffMins = (new Date(curr.timestamp) - new Date(prev.timestamp)) / 60000;
    if (curr.type === prev.type && diffMins < 2) {
      issues.push({ type: "duplicate", severity: "warning", msg: `Registre duplicat: ${getTypeLabel(curr.type)} (${Math.round(diffMins * 60)}s de diferència)` });
    }
  }

  if (sorted.length === 0) return issues;
  const last = sorted[sorted.length - 1];

  // Entry without exit >12h (only if no open shift from yesterday)
  if (last.type === "entry" || last.type === "pause_end") {
    const diffH = (now - new Date(last.timestamp)) / 3600000;
    if (diffH > 12) {
      issues.push({ type: "long_shift", severity: "error", msg: `Entrada sense sortida fa ${Math.round(diffH)}h` });
    }
  }

  // Break open >2h
  if (last.type === "pause_start") {
    const diffH = (now - new Date(last.timestamp)) / 3600000;
    if (diffH > 2) {
      issues.push({ type: "long_break", severity: "warning", msg: `Pausa oberta fa ${Math.round(diffH * 60)} min` });
    }
  }

  // Exit without entry: only flag if there is truly no entry in the full window
  // (covers night shifts: entry yesterday, exit today)
  const hasEntry = sorted.some(r => r.type === "entry");
  const hasExit = sorted.some(r => r.type === "exit");
  if (hasExit && !hasEntry) {
    issues.push({ type: "exit_no_entry", severity: "error", msg: "Sortida registrada sense entrada prèvia" });
  }

  // Pause end without pause start (count globally across both days)
  const pauseStarts = sorted.filter(r => r.type === "pause_start").length;
  const pauseEnds = sorted.filter(r => r.type === "pause_end").length;
  if (pauseEnds > pauseStarts) {
    issues.push({ type: "pause_mismatch", severity: "error", msg: "Fi de pausa sense inici de pausa" });
  }

  return issues;
}

export function calcWorkedMinutes(records) {
  const sorted = [...records].filter(r => !r.is_deleted).sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
  let total = 0;
  let entryTime = null;
  for (const r of sorted) {
    if (r.type === "entry" || r.type === "pause_end") {
      entryTime = new Date(r.timestamp);
    } else if ((r.type === "exit" || r.type === "pause_start") && entryTime) {
      total += (new Date(r.timestamp) - entryTime) / 60000;
      entryTime = null;
    }
  }
  // If still clocked in, add time up to now
  if (entryTime) {
    total += (new Date() - entryTime) / 60000;
  }
  return Math.max(0, Math.round(total));
}

export function minutesToHHMM(mins) {
  if (!mins) return "0h 00m";
  return `${Math.floor(mins / 60)}h ${(mins % 60).toString().padStart(2, "0")}m`;
}

export function getTypeLabel(type) {
  const labels = { entry: "Entrada", exit: "Sortida", pause_start: "Inici pausa", pause_end: "Fi pausa" };
  return labels[type] || type;
}